import { useDispatch } from "react-redux";
import { AppDispatch, useSelector } from "@/core/redux/store";
import { Card } from "@/shared/components/ui/card";
import { useEffect, useMemo } from "react";
import { getActivityLogs } from "@/core/redux/thunks/dashboardThunk";
import { useTranslation } from "react-i18next";
import { formatDuration } from "@/shared/helpers/format-duration";
import AverageTimeCardLoadingState from "./ui-states/loading-state";

const legendItemColors: string[] = [
  "#F76B6D",
  "#F7876B",
  "#F9CF6C",
  "#7BD884",
  "#70BC66",
];

export default function AverageTimeCard() {
  const dispatch = useDispatch<AppDispatch>();
  const { i18n, t } = useTranslation();
  const userDetail = useSelector((state) => state.user.me);
  const company = userDetail?.company;
  const activityLogs = useSelector((state) => state.dashboard.activityLogs);
  const filterParams = useSelector((state) => state.dashboard.filterParams);
  const isLoading = useSelector((state) => state.dashboard.loadingActivityLogs);
  useEffect(() => {
    if (!company?.id) return;
    dispatch(getActivityLogs({ companyId: company.id, ...filterParams }));
  }, [company?.id, filterParams]);

  const totalDuration = useMemo(() => {
    if (!activityLogs) return 0;
    return activityLogs.reduce((sum, log) => sum + log.totalDuration, 0);
  }, [activityLogs]);

  const legendItems = useMemo(() => {
    if (!activityLogs) return [];
    return activityLogs.map((log, index) => {
      return {
        label: t(`dashboard:${log.screenName}`),
        value: formatDuration(i18n.language, log.totalDuration),
        percentage: (log.totalDuration / totalDuration) * 100,
        color: legendItemColors[index],
      };
    });
  }, [activityLogs, totalDuration]);
  if (isLoading) {
    return <AverageTimeCardLoadingState />;
  }
  return (
    <Card className="w-full border border-[#e5e5e5] bg-white rounded-[16px] shadow-[0px_1px_2px_0px_rgba(10,13,20,0.03)]">
      <div className="border-b border-[#e4e4e7] h-[50px] flex items-center px-[20px] py-[16px]">
        <div className="flex items-start flex-1 h-full">
          <p className="text-[16px] font-[700] text-[#000000] leading-[24px]">
            Tedariğe Hazır Olma Süresi
          </p>
        </div>
      </div>
      <div className="flex flex-col gap-[20px] items-start pb-[20px] pt-0 px-[20px] w-full">
        <div className="flex flex-col items-start justify-center w-full">
          <div className="flex gap-[0px] items-start w-full">
            <div className="flex flex-col gap-[4px] items-start mt-[20px]">
              <p className="text-[24px] font-[Inter] font-[700] text-[#171717] leading-[32px]">
                {formatDuration(i18n.language, totalDuration)}
              </p>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-[0px] items-start w-full">
          <div className="flex gap-[2px] items-start w-full">
            {legendItems.map((item, index) => {
              const percentage = item.percentage;
              const isLast = index === legendItems.length - 1;

              return (
                <div
                  key={index}
                  className="relative group cursor-pointer min-w-[4px]"
                  style={{
                    width: isLast ? "auto" : `${percentage}%`,
                    flexGrow: isLast ? 1 : 0,
                  }}
                >
                  <div
                    className="h-[16px] rounded-[2px] w-full"
                    style={{ backgroundColor: item.color }}
                  />
                  {/* Tooltip */}
                  <div
                    className="absolute min-w-[148px] max-w-[228px] -top-10 left-1/2 transform -translate-x-1/2 bg-white rounded-lg shadow-lg px-[11px] py-[7px] border opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10"
                    style={{
                      width: `${item.label.length * 16}px`,
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="w-[4px] h-[12px] rounded-full"
                        style={{ backgroundColor: item.color }}
                      ></div>
                      <div className="flex flex-1 justify-between items-center mt-[3px]">
                        <span className="text-xs text-gray-700">
                          {item.label}
                        </span>
                        <span className="text-xs text-gray-700">
                          {item.value}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <hr className="w-full border-[#e4e4e7]" />
        <div className="flex flex-col gap-[16px] items-start w-full">
          <div className="flex gap-[20px] w-full">
            <div className="text-[#bdb8b8] text-[12px] font-700 flex-1">
              Kaynak
            </div>
            <div className="text-[#bdb8b8] text-[12px] font-700 w-[70px] text-center">
              Yüzde
            </div>
            <div className="text-[#bdb8b8] text-[12px] font-700 w-[70px] text-right">
              Toplam
            </div>
          </div>
          {legendItems.map((item, index) => (
            <div
              key={index}
              className="flex items-center justify-between w-full"
            >
              <div className="flex gap-[6px] items-center flex-1 min-w-0">
                <div
                  className="border-[2px] border-white rounded-full shadow-[0px_2px_4px_0px_rgba(27,28,29,0.04)] size-[12px] shrink-0"
                  style={{ backgroundColor: item.color }}
                />
                <div className="text-[14px] font-[600] text-[rgba(63,63,70,0.7)] leading-[20px] tracking-[-0.3px] truncate">
                  {item.label}
                </div>
              </div>
              <div className="font-[Inter] text-[14px] font-[500] w-[70px] text-[#A1A1AA] leading-[20px] tracking-[-0.084px]">
                {item.percentage ? Math.round(item.percentage) : 0}%
              </div>
              <div className="text-[14px] font-[700] w-[70px] text-right text-[rgba(63,63,70,0.8)] leading-[20px] shrink-0">
                {item.value}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}
